import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { differenceInDays, format } from "date-fns";
import { Bell, Calendar, Pencil, Plus } from "lucide-react";
import { cn, getStatusColor, getPriorityColor } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CreateReminderForm } from "@/components/forms/CreateReminderForm";
import { AssetRecordForm } from "@/components/forms/AssetRecordForm";
import { toast } from "@/components/ui/use-toast";
import type { Reminder } from "@/types/reminders";
import { Link } from "react-router-dom";

export interface RemindersAllProps {
  reminders: Reminder[];
  currentPage: number;
  totalPages: number;
  onPreviousPage: () => void;
  onNextPage: () => void;
  hasMore: boolean;
  totalCount: number;
  pageSize: number;
  onRecordAdded: () => void;
  onEditClick: (reminder: Reminder) => void;
  showAssetLink?: boolean;
}

export function RemindersAll({
  reminders,
  currentPage,
  totalPages,
  onPreviousPage,
  onNextPage,
  hasMore,
  totalCount,
  pageSize,
  onRecordAdded,
  onEditClick,
  showAssetLink
}: RemindersAllProps) {
  const { t } = useTranslation();
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState<Reminder | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [editDialogState, setEditDialogState] = useState<{
    isOpen: boolean;
    reminder: Reminder | null;
  }>({
    isOpen: false,
    reminder: null,
  });

  const { data: userAssets, refetch: refetchAssets } = useQuery<any[]>({
    queryKey: ['userAssets'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('assets')
        .select('id, name, asset_name')
        .eq('user_id', user.id);

      if (error) throw error;
      return data;
    }
  });

  const handleCompleteClick = (reminder: Reminder) => {
    setSelectedReminder(reminder);
    setShowCompleteDialog(true);
  };

  const handleCompleteSuccess = async () => {
    if (!selectedReminder) return;

    try {
      // Calculate next trigger date based on recurrency
      const nextTriggerDate = new Date();
      switch (selectedReminder.recurrency_unit) {
        case 'days':
          nextTriggerDate.setDate(nextTriggerDate.getDate() + selectedReminder.recurrency_value);
          break;
        case 'weeks':
          nextTriggerDate.setDate(nextTriggerDate.getDate() + (selectedReminder.recurrency_value * 7));
          break;
        case 'months':
          nextTriggerDate.setMonth(nextTriggerDate.getMonth() + selectedReminder.recurrency_value);
          break;
        case 'years':
          nextTriggerDate.setFullYear(nextTriggerDate.getFullYear() + selectedReminder.recurrency_value);
          break;
      }

      // Update the reminder with new next_trigger_at and last_triggered_at
      const { error } = await supabase
        .from('reminders')
        .update({
          next_trigger_at: nextTriggerDate.toISOString(),
          last_triggered_at: new Date().toISOString()
        })
        .eq('id', selectedReminder.id);

      if (error) throw error;

      setShowCompleteDialog(false);
      setSelectedReminder(null);
      refetchAssets();
      onRecordAdded();
      toast({
        title: t('common.success'),
        description: t('reminders.updateSuccess'),
      });
    } catch (error) {
      console.error('Error updating reminder:', error);
      toast({
        title: t('common.error'),
        description: t('reminders.updateError'),
        variant: 'destructive',
      });
    }
  };

  const handleReminderSuccess = () => {
    setShowReminderDialog(false);
    setSelectedAssetId(null);
    refetchAssets();
    onRecordAdded();
    toast({
      title: t('common.success'),
      description: t('reminders.createSuccess'),
    });
  };

  const handleEditClick = (reminder: Reminder) => {
    setEditDialogState({
      isOpen: true,
      reminder,
    });
  };

  const handleEditSuccess = () => {
    setEditDialogState({
      isOpen: false,
      reminder: null,
    });
    refetchAssets();
    onRecordAdded();
    toast({
      title: t('common.success'),
      description: t('reminders.updateSuccess'),
    });
  };

  const handleEditCancel = () => {
    setEditDialogState({
      isOpen: false,
      reminder: null,
    });
  };

  const getDueStatusColor = (nextTriggerAt: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const triggerDate = new Date(nextTriggerAt);
    triggerDate.setHours(0, 0, 0, 0);
    const daysDiff = differenceInDays(triggerDate, today);

    if (triggerDate.getTime() === today.getTime()) {
      return "bg-green-50 border-green-200";
    } else if (daysDiff < -7) {
      return "bg-red-50 border-red-200";
    } else if (daysDiff < 0) {
      return "bg-yellow-50 border-yellow-200";
    }
    return "bg-white";
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <div className="text-sm text-apple-gray">
          {totalCount} {t('reminders.title')}
        </div>
        {userAssets?.length > 0 && (
          <Button
            onClick={() => setShowReminderDialog(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('reminders.new')}
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {reminders?.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-apple-gray">{t('reminders.noReminders')}</p>
          </div>
        ) : (
          reminders?.map((reminder) => (
            <div
              key={reminder.id}
              className={cn(
                "p-4 rounded-apple",
                getDueStatusColor(reminder.next_trigger_at),
                "border border-apple-light-gray/50",
                "space-y-4",
                "shadow-md hover:shadow-lg transition-shadow duration-200"
              )}
            >
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <h4 className="font-medium text-apple-dark">{reminder.title}</h4>
                  {reminder.description && (
                    <p className="text-sm text-apple-gray">{reminder.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditClick(reminder)}
                    className="text-apple-gray hover:text-apple-dark"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Badge variant="outline" className={cn("text-xs", getStatusColor(reminder.status))}>
                    {reminder.status}
                  </Badge>
                </div>
              </div>

              {/* Asset Information */}
              {reminder.assets && (
                <div className="space-y-1.5 border-l-2 border-apple-light-gray/30 pl-3">
                  <div className="text-xs uppercase tracking-wider text-apple-gray mb-1">
                    {t('records.assetDetails')}
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-apple-blue">
                        {reminder.assets.asset_name || reminder.assets.name}
                      </span>
                    </div>
                    {reminder.assets.company && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-apple-gray">
                          {t('records.company')}: {reminder.assets.company.name}
                        </span>
                      </div>
                    )}
                    {reminder.assets.place && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-apple-gray/80">
                          {t('records.place')}: {reminder.assets.place.name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="text-xs">
                  {t('reminders.every')} {reminder.recurrency_value} {reminder.recurrency_unit}
                </Badge>
                {reminder.priority && (
                  <Badge variant="outline" className={cn("text-xs", getPriorityColor(reminder.priority))}>
                    {reminder.priority}
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-4 text-sm text-apple-gray">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>{format(new Date(reminder.next_trigger_at), 'MMM d, yyyy')}</span>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleCompleteClick(reminder)}
                  className="ml-auto"
                >
                  {t('reminders.complete')}
                </Button>
                {showAssetLink && reminder.assets && (
                  <Link to={`/assets/${reminder.assets.id}`}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-blue-50 hover:bg-blue-100 text-blue-600"
                    >
                      {t('reminders.goToAsset')}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Reminder Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent className="sm:max-w-[600px] h-[90vh] flex flex-col">
          <div className="flex flex-col h-full">
            <DialogHeader>
              <DialogTitle>{t('reminders.createReminder')}</DialogTitle>
              <DialogDescription>
                {t('reminders.createReminderDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              {!selectedAssetId ? (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Select an Asset</h3>
                  <div className="grid gap-2">
                    {userAssets?.map((asset) => (
                      <Button
                        key={asset.id}
                        variant="outline"
                        className="justify-start"
                        onClick={() => setSelectedAssetId(asset.id)}
                      >
                        {asset.name || asset.asset_name || `Asset ${asset.id}`}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <CreateReminderForm
                  assetId={selectedAssetId}
                  onSuccess={handleReminderSuccess}
                  onCancel={() => {
                    setShowReminderDialog(false);
                    setSelectedAssetId(null);
                  }}
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete Reminder Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="sm:max-w-[600px] h-[90vh] flex flex-col">
          <div className="flex flex-col h-full">
            <DialogHeader>
              <DialogTitle>{t('records.completeReminder')}</DialogTitle>
              <DialogDescription>
                {t('records.completeReminderDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              <AssetRecordForm
                assetId={selectedReminder?.asset_id}
                mode="create"
                onSuccess={handleCompleteSuccess}
                onCancel={() => {
                  setShowCompleteDialog(false);
                  setSelectedReminder(null);
                }}
                initialData={{
                  title: selectedReminder?.title,
                  details: selectedReminder?.description,
                  type: "Maintenance",
                  status: "Completed",
                  priority: selectedReminder?.priority,
                }}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Reminder Dialog */}
      <Dialog open={editDialogState.isOpen} onOpenChange={(open) => !open && handleEditCancel()}>
        <DialogContent className="sm:max-w-[600px] h-[90vh] flex flex-col">
          <div className="flex flex-col h-full">
            <DialogHeader>
              <DialogTitle>{t('reminders.editReminder')}</DialogTitle>
              <DialogDescription>
                {t('reminders.editReminderDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              <CreateReminderForm
                assetId={editDialogState.reminder?.asset_id}
                mode="edit"
                initialData={editDialogState.reminder}
                onSuccess={handleEditSuccess}
                onCancel={handleEditCancel}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
} 
